import React, { memo } from 'react';

import { MuiThemeProvider } from '@schoolinks/ui-components';
import * as Sentry from '@sentry/react';
import i18n from 'i18next';
import HttpApi from 'i18next-http-backend';

import { isEqual } from 'lodash';
import { shape, func } from 'prop-types';
import { CookiesProvider } from 'react-cookie';
import { initReactI18next } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import DayJsUtils from '@mui/lab/AdapterDayjs';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import CssBaseline from '@mui/material/CssBaseline';

import ErrorBox from 'components/Common/ErrorBox/ErrorBox';
import FullStory from 'components/Common/FullStory';
import Intercom from 'components/Common/Intercom';
import { isElementaryPage } from 'components/Common/LayoutV2/utils';
import LoadingOverlay from 'components/Common/LoadingOverlay';
import UnsupportedBrowserInterrupt from 'components/Modals/UnsupportedBrowserInterrupt';
import { DEBUG_I18N } from 'config_app';
import useAccessibility from 'hooks/useAccessibility';
import useAppConfig from 'hooks/useAppConfig';
import useMediaInfo from 'hooks/useMediaInfo';
import useUserContext from 'hooks/useUserContext';
import createDebugTranslation from 'i18n/createDebugTranslation';
import enTranslations from 'locales/en.json';
import ReactQueryProvider from 'providers/reactQuery';
import { UtilsProvider } from 'providers/utils';
import RoutesWrapper from 'routes';
import { isSentryEnabledForEnv } from 'services/initSentry';
import DragDropProvider from 'utils/DragDropProvider';
import '@schoolinks/ui-components/src/utils/variables.css';

i18n
  .use(HttpApi)
  .use(initReactI18next)
  .init({
    lng: 'en',
    fallbackLng: 'en',
    // Define two namespaces:
    //   1) "translation"    -> loaded from backend
    //   2) "fallback"       -> your local file
    ns: ['translation', 'fallback'],
    // i18n will look in "translation" first and fallback to "fallback"
    // if the key is missing in "translation"
    defaultNS: 'translation',
    fallbackNS: 'fallback',
    partialBundledLanguages: true,
    resources: {
      en: {
        fallback: enTranslations,
      },
    },
    interpolation: {
      escapeValue: false,
    },
    load: 'currentOnly',
  })
  .then(() => {
    if (DEBUG_I18N) {
      const originalT = i18n.t.bind(i18n);
      i18n.t = (key, options) => {
        if (options?.debugAsText) {
          return `_${originalT(key, options)}_`;
        }
        return createDebugTranslation(originalT)(key, options);
      };
    }
  });

/**
 * @prop {ReduxStore} store -> store object of redux
 * @prop {UserReducerObject} user -> user reducer object from redux store
 * @returns
 */
const App = memo(
  ({ store }) => {
    const { isMobile, isTablet, isUnsupported } = useMediaInfo();
    const {
      affiliations,
      isLoggedIn,
      isMember,
      moduleAccess,
      moduleAccessLoaded,
    } = useSelector(({ user }) => user);

    /*
        Handles important events for users, handles them with useEffect life-cycle hook on mount, and unmount
        - Responsive support on window re-sizing
        - Giving news for users if there's a new version
     */
    useAppConfig();
    /*
        Loads the layout/context of the user depending on given logic. 
        Uses redux actions to operate these asynchronous data operations.
    */
    useUserContext({
      data: { isLoggedIn, isMember, moduleAccess, affiliations },
    });

    useAccessibility();

    if (__DEV__) window.user = store.getState().user;
    if (!moduleAccessLoaded) {
      return <LoadingOverlay />;
    }

    const activeEnvironment = ENVIRONMENT;
    const isErrorBoxVisible =
      isSentryEnabledForEnv(activeEnvironment) && activeEnvironment !== 'qa';
    const hideIntercom =
      isMobile || (isElementaryPage(window.location.pathname) && isTablet);

    return (
      <ReactQueryProvider>
        <UtilsProvider>
          <LocalizationProvider dateAdapter={DayJsUtils}>
            <MuiThemeProvider>
              <CookiesProvider>
                <DragDropProvider>
                  <CssBaseline />
                  <Sentry.ErrorBoundary
                    fallback={isErrorBoxVisible ? ErrorBox : null}
                  >
                    <BrowserRouter>
                      <RoutesWrapper />
                    </BrowserRouter>
                  </Sentry.ErrorBoundary>
                  {isUnsupported && <UnsupportedBrowserInterrupt />}
                  {!hideIntercom ? <Intercom /> : null}
                  <FullStory />
                </DragDropProvider>
              </CookiesProvider>
            </MuiThemeProvider>
          </LocalizationProvider>
        </UtilsProvider>
      </ReactQueryProvider>
    );
  },
  // We shouldn't update the component if props haven't changed
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
);

App.propTypes = {
  store: shape({
    getState: func.isRequired,
  }).isRequired,
};

export default App;
